import React, {useState, useMemo, useCallback} from 'react';
import {PaymentMethodModel, PaymentSystemType, PaymentOperationType} from '../../../models/paymentMethod';
import {Box, Typography, Chip, Avatar, Table, Button, IconButton, Tooltip} from '@mui/joy';
import {useNavigate} from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {PaymentMethodsApi} from './paymentMethodsApi';
import ConfirmDialog from '../../../components/confirmDialog/ConfirmDialog';
import { kopecksToRublesNumber } from '../../../common/money';

const getSystemTypeName = (systemType: PaymentSystemType): string => {
    switch (systemType) {
        case PaymentSystemType.Tome:
            return 'Tome';
        case PaymentSystemType.GameMoney:
            return 'GameMoney';
        default:
            return String(systemType);
    }
};

interface PaymentMethodsTableProps {
    operationType: PaymentOperationType;
    systemType?: PaymentSystemType;
    currencies?: string[];
    searchTitle?: string;
    allPaymentMethods: PaymentMethodModel[];
    onDeleted?: (id: number) => void;
}

export function PaymentMethodsTable({operationType, systemType, currencies, searchTitle, allPaymentMethods, onDeleted}: PaymentMethodsTableProps) {
    const navigate = useNavigate();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

    const requestDelete = useCallback((id: number) => {
        setPendingDeleteId(id);
        setConfirmOpen(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (pendingDeleteId == null) return;
        const ok = await PaymentMethodsApi.deletePaymentMethod(pendingDeleteId);
        if (ok) {
            if (onDeleted) onDeleted(pendingDeleteId);
        }
        setPendingDeleteId(null);
    }, [pendingDeleteId, onDeleted]);


    // Отфильтрованные данные (мемоизировано)
    const filtered = useMemo(() => {
        let list = allPaymentMethods;
        list = list.filter(m => m.operationType === operationType);
        if (systemType !== undefined) list = list.filter(m => m.systemType === systemType);
        if (currencies && currencies.length) list = list.filter(m => (m.currencies ?? []).some(c => currencies!.includes(c)));
        if (searchTitle && searchTitle.trim() !== '') {
            const s = searchTitle.toLowerCase();
            list = list.filter(m => m.titleRu.toLowerCase().includes(s) || (m.titleEn && m.titleEn.toLowerCase().includes(s)));
        }
        return list;
    }, [allPaymentMethods, operationType, systemType, currencies, searchTitle]);

    // Десктопная версия - таблица
    const desktopTable = (
        <Box sx={{ overflow: 'auto', borderRadius: 8, mt: 1, display: { xs: 'none', sm: 'block' } }}>
            <Table aria-label="payment-methods" stickyHeader>
                <thead>
                    <tr>
                        <th style={{ width: 80 }}>ID</th>
                        <th style={{ width: 80 }}>Статус</th>
                        <th style={{ width: 80 }}>Логотип</th>
                        <th>Название</th>
                        <th style={{ width: 100 }}>Валюта</th>
                        <th style={{ width: 150 }}>Суммы</th>
                        <th style={{ width: 150 }}>Комиссия</th>
                        <th style={{ width: 100 }}>Сортировка</th>
                        <th style={{ width: 120 }}>Внешний ID</th>
                        {operationType === PaymentOperationType.Withdraw && (
                            <th style={{ width: 120 }}>Расчет скорости</th>
                        )}
                        <th style={{ width: 120 }}>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map(method => (
                        <tr key={method.id}>
                            <td>{method.id}</td>
                            <td>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: method.isActive ? 'success.500' : 'danger.500' }} />
                                </Box>
                            </td>
                            <td>
                                <Avatar 
                                    src={method.image} 
                                    alt={method.titleRu}
                                    sx={{width: 40, height: 40}}
                                />
                            </td>
                            <td>
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold">
                                        {method.titleRu}
                                    </Typography>
                                    {method.titleEn && (
                                        <Typography level="body-xs" color="neutral">
                                            {method.titleEn}
                                        </Typography>
                                    )}
                                </Box>
                            </td>
                            <td>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {(method.currencies ?? []).map(c => (
                                        <Chip key={c} size="sm" variant="soft" color="primary">{c}</Chip>
                                    ))}
                                </Box>
                            </td>
                            <td>
                                <Box>
                                    <Typography level="body-sm">
                                        Мин: {kopecksToRublesNumber(method.minAmount).toFixed(2).replace(/\./g, ',')}
                                    </Typography>
                                    <Typography level="body-sm">
                                        Макс: {kopecksToRublesNumber(method.maxAmount).toFixed(2).replace(/\./g, ',')}
                                    </Typography>
                                </Box>
                            </td>
                            <td>
                                <Box>
                                    <Typography level="body-sm">
                                        Наша: {method.ourCommissionPercent.toFixed(2)}%
                                    </Typography>
                                    <Typography level="body-sm">
                                        {getSystemTypeName(method.systemType)}: {method.providerCommissionPercent.toFixed(2)}%{method.providerFixedCommission>0?` + ${new Intl.NumberFormat().format(kopecksToRublesNumber(method.providerFixedCommission))}`:''}{method.providerMinCommission && method.providerMinCommission>0?` (мин: ${new Intl.NumberFormat().format(kopecksToRublesNumber(method.providerMinCommission))})`:''}
                                    </Typography>
                                </Box>
                            </td>
                            <td>{method.sort}</td>
                            <td>
                                <Typography level="body-sm" fontFamily="monospace">
                                    {method.externalRef && method.externalRef.trim() !== '' ? method.externalRef : '-'}
                                </Typography>
                            </td>
                            {operationType === PaymentOperationType.Withdraw && (
                                <td>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: method.calculateWithdrawalTime ? 'success.500' : 'neutral.400' }} />
                                    </Box>
                                </td>
                            )}
                            <td>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Редактировать">
                                        <IconButton size="sm" variant="outlined"
                                                    onClick={() => navigate(`/app/payment-methods/${method.id}/edit`, { state: { method } })}>
                                            <EditIcon/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Удалить">
                                        <IconButton size="sm" variant="outlined" color="danger"
                                                    onClick={() => requestDelete(method.id)}>
                                            <DeleteIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Box>
    );

    // Мобильная версия - карточки
    const mobileCards = (
        <Box sx={{display: {xs: 'block', sm: 'none'}}}>
            {filtered.map((method) => (
                <Box
                    key={method.id}
                    sx={{
                        bgcolor: 'background.level1',
                        borderRadius: 8,
                        p: 2,
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    {/* Заголовок карточки */}
                    <Box sx={{mb: 2}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                            <Avatar 
                                src={method.image} 
                                alt={method.titleRu}
                                sx={{width: 40, height: 40, flexShrink: 0}}
                            />
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Typography level="title-sm" fontWeight="bold" sx={{
                                    mb: 0.5,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word'
                                }}>
                                    {method.titleRu}
                                </Typography>
                                {method.titleEn && (
                                    <Typography level="body-xs" color="neutral" sx={{
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word'
                                    }}>
                                        {method.titleEn}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Редактировать">
                                    <IconButton size="sm" variant="outlined"
                                                onClick={() => navigate(`/app/payment-methods/${method.id}/edit`, { state: { method } })}>
                                        <EditIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Удалить">
                                    <IconButton size="sm" variant="outlined" color="danger"
                                                onClick={() => requestDelete(method.id)}>
                                        <DeleteIcon/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: method.isActive ? 'success.500' : 'danger.500' }} />
                                <Typography level="body-xs" color="neutral">{method.isActive ? 'Активен' : 'Отключен'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {(method.currencies ?? []).map(c => (
                                    <Chip key={c} size="sm" variant="soft" color="primary">{c}</Chip>
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    {/* Основная информация */}
                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2}}>
                        <Box>
                            <Typography level="body-xs" color="neutral" sx={{mb: 0.5}}>
                                ID
                            </Typography>
                            <Typography level="body-sm" fontWeight="bold">
                                {method.id}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography level="body-xs" color="neutral" sx={{mb: 0.5}}>
                                Сортировка
                            </Typography>
                            <Typography level="body-sm" fontWeight="bold">
                                {method.sort}
                            </Typography>
                        </Box>
                                                 <Box>
                             <Typography level="body-xs" color="neutral" sx={{mb: 0.5}}>
                                 Мин. сумма
                             </Typography>
                             <Typography level="body-sm" fontWeight="bold">
                                 {kopecksToRublesNumber(method.minAmount).toFixed(2).replace(/\./g, ',')}
                             </Typography>
                         </Box>
                         <Box>
                             <Typography level="body-xs" color="neutral" sx={{mb: 0.5}}>
                                 Макс. сумма
                             </Typography>
                             <Typography level="body-sm" fontWeight="bold">
                                 {kopecksToRublesNumber(method.maxAmount).toFixed(2).replace(/\./g, ',')}
                             </Typography>
                         </Box>
                    </Box>

                    {/* Комиссии */}
                    <Box sx={{mb: 2}}>
                        <Typography level="body-xs" color="neutral" sx={{mb: 1}}>
                            Комиссии
                        </Typography>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <Box>
                                <Typography level="body-xs" color="neutral">
                                    Наша
                                </Typography>
                                <Typography level="body-sm" fontWeight="bold" color="success">
                                    {method.ourCommissionPercent.toFixed(2)}%
                                </Typography>
                            </Box>
                            <Box>
                                <Typography level="body-xs" color="neutral">
                                    {getSystemTypeName(method.systemType)}
                                </Typography>
                                <Typography level="body-sm" fontWeight="bold" color="warning">
                                    {method.providerCommissionPercent.toFixed(2)}%{method.providerFixedCommission>0?` + ${new Intl.NumberFormat().format(kopecksToRublesNumber(method.providerFixedCommission))}`:''}{method.providerMinCommission && method.providerMinCommission>0?` (мин: ${new Intl.NumberFormat().format(kopecksToRublesNumber(method.providerMinCommission))})`:''}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Внешний ID */}
                    {method.externalRef && method.externalRef.trim() !== '' && (
                        <Box sx={{mb: 2}}>
                            <Typography level="body-xs" color="neutral" sx={{mb: 0.5}}>
                                Внешний ID
                            </Typography>
                            <Typography level="body-xs" fontFamily="monospace" sx={{
                                bgcolor: 'background.level2',
                                p: 1,
                                borderRadius: 4,
                                wordBreak: 'break-all'
                            }}>
                                {method.externalRef}
                            </Typography>
                        </Box>
                    )}

                    {/* Расчет скорости выводов (только для выводов) */}
                    {operationType === PaymentOperationType.Withdraw && (
                        <Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: method.calculateWithdrawalTime ? 'success.500' : 'neutral.400' }} />
                                <Typography level="body-xs" color="neutral">
                                    Расчет скорости выводов: {method.calculateWithdrawalTime ? 'Включен' : 'Отключен'}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            ))}
        </Box>
    );

    return (
        <Box sx={{width: '100%'}}>
            <ConfirmDialog
                title="Подтверждение"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={confirmDelete}
            >
                Вы действительно хотите удалить метод оплаты?
            </ConfirmDialog>
            {desktopTable}
            {mobileCards}
        </Box>
    );
}
